import type {
  customerAiIntroductionSkillsTable,
  customerReportIntegrationsTable,
  customersTable,
} from '@/api-server/services/db/schema/customers';
import { isLanguageCode, LanguageCode } from './language';
import { ReportType } from './reports';

export type AiIntroduction = Pick<
  typeof customerAiIntroductionSkillsTable.$inferSelect,
  'policySlides' | 'videoUrl'
>;
export type PolicySlide = { markdown: string; buttonText: string };
export type PolicySlides = Record<LanguageCode, PolicySlide[]>;
export type CustomerReportConfig = Pick<
  typeof customerReportIntegrationsTable.$inferInsert,
  'spreadsheetId' | 'dataSheetName'
>;

export type Customer = typeof customersTable.$inferSelect;
export type CustomerConfig = Customer & {
  aiIntroduction: AiIntroduction;
  reports: Record<ReportType, CustomerReportConfig>;
  pdfDownloadUrl: string;
};

export const toAiIntroduction = (aiIntroduction: AiIntroduction | unknown): AiIntroduction => {
  if (typeof aiIntroduction !== 'object' || aiIntroduction === null) return toAiIntroduction({});
  return {
    policySlides: toPolicySlides((aiIntroduction as AiIntroduction)?.policySlides ?? []),
    videoUrl: ((aiIntroduction as AiIntroduction)?.videoUrl ?? '').trim(),
  };
};

export const toReportConfig = (
  reportConfig: CustomerReportConfig | unknown,
): CustomerReportConfig => {
  if (typeof reportConfig !== 'object' || reportConfig === null) return toReportConfig({});
  return {
    spreadsheetId: ((reportConfig as CustomerReportConfig)?.spreadsheetId ?? '').trim(),
    dataSheetName: ((reportConfig as CustomerReportConfig)?.dataSheetName ?? '').trim(),
  };
};

/**
 * Ensure that the input is a valid PolicySlides object with the expected languages.
 */
export const toPolicySlides = (slides: PolicySlides | unknown): PolicySlides => {
  const result: PolicySlides = {
    [LanguageCode.English]: [],
    [LanguageCode.Portuguese]: [],
    [LanguageCode.Spanish]: [],
    [LanguageCode.German]: [],
    [LanguageCode.French]: [],
    [LanguageCode.Arabic]: [],
    [LanguageCode.Korean]: [],
    [LanguageCode.Italian]: [],
  };
  if (typeof slides !== 'object' || slides === null) return result;
  Object.entries(slides).forEach(([lang, items]) => {
    if (!isLanguageCode(lang) || !Array.isArray(items)) return;
    const validItems = items.filter(isPolicySlide);
    result[lang] = validItems.map((item) => ({
      markdown: item.markdown ?? '',
      buttonText: item.buttonText ?? '',
    }));
  });
  return result;
};

export const isPolicySlide = (slide: PolicySlide | unknown): slide is PolicySlide => {
  if (typeof slide !== 'object' || slide === null) return false;
  return (
    Object.prototype.hasOwnProperty.call(slide, 'markdown') &&
    Object.prototype.hasOwnProperty.call(slide, 'buttonText') &&
    (!(slide as PolicySlide).markdown || typeof (slide as PolicySlide).markdown === 'string') &&
    (!(slide as PolicySlide).buttonText || typeof (slide as PolicySlide).buttonText === 'string')
  );
};

export const customerFlags = (customer?: Partial<Customer>): Omit<Customer, 'name' | 'id'> => ({
  allowExerciseSkipping: !!customer?.allowExerciseSkipping,
  hideBeta: !!customer?.hideBeta,
  enableAiIntroduction: !!customer?.enableAiIntroduction,
  demoOnboardingFeedback: !!customer?.demoOnboardingFeedback,
  enableAIAcademyLink: !!customer?.enableAIAcademyLink,
  enablePdfDownload: !!customer?.enablePdfDownload,
  syncUserProgramCompletion: !!customer?.syncUserProgramCompletion,
  enablePlatformBeta: !!customer?.enablePlatformBeta,
  showDisabledLanguages: !!customer?.showDisabledLanguages,
});
